import { useState } from 'react';
import type { Category, Product } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { productsApi, productStockApi } from '../../api/resources.js';
import type { CreateProductRequestWithStock } from '../../api/types.js';
import { ApiError } from '../../lib/apiClient.js';
import { Modal } from '../../components/Modal.js';
import { ImageUploadField } from './ImageUploadField.js';
import { PriceInput } from './PriceInput.js';
import { StockInput } from './StockInput.js';
import { useEscToClose } from './useEscToClose.js';
import { ProductOptionsEditor } from './ProductOptionsEditor.js';
import {
  draftsFromProduct,
  lowestActivePrice,
  optionsProblem,
  reconcileDrafts,
  stockAdjustments,
  toOptionInputs,
  type OptionDraft,
} from './productOptionsDraft.js';

export interface ProductFormModalProps {
  /** `null` creates a new product; a real row opens full edit. */
  product: Product | null;
  /**
   * Seeds a NEW product's fields from an existing one, for the "Duplicar"
   * action — speeds up adding a variant (same category/price/image, a
   * different SKU and usually a tweaked name). Ignored whenever `product`
   * is set (an edit always wins). The SKU is deliberately NOT copied
   * as-is — `products.sku` is unique on live rows (`products_sku_live_idx`)
   * and reusing it outright would just 409; a numbered suggestion is
   * seeded instead so there's something to edit rather than an empty
   * required field. Stock also never carries over: a duplicate is a new,
   * physically distinct product with its own (zero) stock, never the
   * source's existing count.
   */
  duplicateFrom?: Product | null;
  categories: Category[];
  onClose(): void;
  onSaved(product: Product): void;
}

/**
 * Full product edit — name, SKU, category, price, image, sort order, active,
 * and a stock adjustment — as well as creation, which additionally accepts an
 * opening stock quantity. The two used to be separate flows (a create-only
 * modal, and an inline table-only price edit); this is the "full edit modal"
 * the client asked for.
 */
export function ProductFormModal({ product, duplicateFrom, categories, onClose, onSaved }: ProductFormModalProps) {
  const { t } = useI18n();
  const seed = product ?? duplicateFrom ?? null;
  const [sku, setSku] = useState(product ? product.sku : duplicateFrom ? `${duplicateFrom.sku}-2` : '');
  const [name, setName] = useState(seed?.name ?? '');
  const [categoryId, setCategoryId] = useState(seed?.categoryId ?? '');
  const [priceCents, setPriceCents] = useState<number | null>(seed?.priceCents ?? null);
  const [costCents, setCostCents] = useState<number | null>(seed?.costCents ?? null);
  const [costValid, setCostValid] = useState(true);
  const [priceValid, setPriceValid] = useState(true);
  const [imageUrl, setImageUrl] = useState(seed?.imageUrl ?? '');
  const [sortOrder, setSortOrder] = useState(seed?.sortOrder ?? 0);
  const [needsKitchen, setNeedsKitchen] = useState(seed?.needsKitchen ?? true);
  const [active, setActive] = useState(product?.active ?? true);
  const [stockQty, setStockQty] = useState<number | null>(product?.stockQty ?? 0);
  const [stockValid, setStockValid] = useState(true);
  // Options (flavours). A duplicate starts from the source's options too (same
  // names and prices), but never its stock: a duplicate is physically new.
  const [optionsEnabled, setOptionsEnabled] = useState((seed?.options?.length ?? 0) > 0);
  const [optionsLabel, setOptionsLabel] = useState(seed?.optionsLabel ?? '');
  const [optionsMax, setOptionsMax] = useState(seed?.optionsMax != null ? String(seed.optionsMax) : '');
  const [addonsMax, setAddonsMax] = useState(seed?.addonsMax != null ? String(seed.addonsMax) : '');
  const [mixMin, setMixMin] = useState(seed?.mixMinFlavours != null ? String(seed.mixMinFlavours) : '');
  const [mixPriceCents, setMixPriceCents] = useState<number | null>(seed?.mixPriceCents ?? null);
  const [mixPriceValid, setMixPriceValid] = useState(true);
  const [optionDrafts, setOptionDrafts] = useState<OptionDraft[]>(() => {
    const drafts = draftsFromProduct(seed);
    return product ? drafts : drafts.map(({ id: _id, ...d }) => ({ ...d, stockQty: 0, originalStock: 0 }));
  });
  // The product as it exists on the server. A create that succeeds and then fails
  // later (an option's stock call) leaves the form editing THIS, so a retry updates
  // it instead of creating a second product.
  const [persisted, setPersisted] = useState<Product | null>(product);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscToClose(onClose, saving);

  const category = categories.find((c) => c.id === categoryId);
  const hadOptions = (persisted?.options?.length ?? 0) > 0;
  const optionsOk = !optionsEnabled || optionsProblem(optionDrafts) === null;
  // With options, the product's own price is the lowest active option price,
  // so the price field is not asked for (and not required).
  const derivedPrice = optionsEnabled ? lowestActivePrice(optionDrafts) : null;
  const effectivePrice = optionsEnabled ? derivedPrice : priceCents;
  const maxNumber = optionsMax.trim() === '' ? null : Number(optionsMax);
  const maxOk = maxNumber === null || (Number.isInteger(maxNumber) && maxNumber >= 1);
  const addonsMaxNumber = addonsMax.trim() === '' ? null : Number(addonsMax);
  const addonsMaxOk = addonsMaxNumber === null || (Number.isInteger(addonsMaxNumber) && addonsMaxNumber >= 1);
  // The mix price is a pair: both filled (count >= 2, a valid price) or both empty.
  const mixMinNumber = mixMin.trim() === '' ? null : Number(mixMin);
  const mixOk =
    mixPriceValid &&
    ((mixMinNumber === null && mixPriceCents === null) ||
      (mixMinNumber !== null && Number.isInteger(mixMinNumber) && mixMinNumber >= 2 && mixPriceCents !== null));
  const canSave =
    Boolean(sku.trim()) &&
    Boolean(name.trim()) &&
    effectivePrice != null &&
    (optionsEnabled || priceValid) &&
    costValid &&
    (optionsEnabled || stockValid) &&
    optionsOk &&
    maxOk &&
    addonsMaxOk &&
    mixOk &&
    !saving;

  /** What the request says about options: the full set, or [] to retire them all. */
  function optionFields() {
    if (!optionsEnabled) return hadOptions ? { options: [], optionsLabel: null, optionsMax: null, addonsMax: null, mixMinFlavours: null, mixPriceCents: null } : {};
    return {
      optionsLabel: optionsLabel.trim() || null,
      optionsMax: maxNumber,
      addonsMax: optionDrafts.some((d) => d.isAddon) ? addonsMaxNumber : null,
      mixMinFlavours: mixMinNumber,
      mixPriceCents: mixMinNumber === null ? null : mixPriceCents,
      options: toOptionInputs(optionDrafts),
    };
  }

  async function submit() {
    if (!canSave || effectivePrice == null) return;
    const priceToSend = effectivePrice;
    setSaving(true);
    setError(null);
    let optionsStep = false;
    try {
      let saved: Product;
      if (persisted) {
        saved = await productsApi.update(persisted.id, {
          sku: sku.trim(),
          name: name.trim(),
          categoryId: categoryId || null,
          priceCents: priceToSend,
          costCents,
          imageUrl: imageUrl.trim() || null,
          sortOrder,
          active,
          needsKitchen,
          ...optionFields(),
        });
        // Stock never travels through the generic update body above — see
        // routes/products.ts. A dedicated call only when the value actually
        // changed, so leaving the field untouched never writes a no-op
        // movement.
        if (!optionsEnabled && stockQty != null && stockQty !== persisted.stockQty) {
          saved = await productStockApi.adjust(persisted.id, stockQty);
        }
      } else {
        const body: CreateProductRequestWithStock = {
          sku: sku.trim(),
          name: name.trim(),
          categoryId: categoryId || null,
          priceCents: priceToSend,
          costCents,
          imageUrl: imageUrl.trim() || null,
          sortOrder,
          needsKitchen,
          // A product sold by options counts its stock on the options, not on itself.
          stockQty: optionsEnabled ? 0 : (stockQty ?? 0),
          ...optionFields(),
        };
        saved = await productsApi.create(body);
      }
      // Option stock has its own endpoint, same as product stock: one ledger
      // row per option whose count actually changed.
      setPersisted(saved);
      if (optionsEnabled) {
        const reconciled = reconcileDrafts(optionDrafts, saved.options ?? []);
        setOptionDrafts(reconciled);
        optionsStep = true;
        for (const adj of stockAdjustments(reconciled, saved.options ?? [])) {
          saved = await productStockApi.adjustOption(saved.id, adj.optionId, adj.stockQty);
          setPersisted(saved);
          // This option's count is now the server's; a retry must not redo it.
          setOptionDrafts((current) =>
            current.map((d) => (d.id === adj.optionId ? { ...d, originalStock: adj.stockQty } : d)),
          );
        }
      }
      onSaved(saved);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('common.error');
      // The product itself is saved by now: say so, so nobody re-creates it.
      setError(optionsStep ? `${t('products.options.savedButStock')} ${message}` : message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={persisted ? t('products.editTitle') : duplicateFrom ? t('products.duplicateTitle') : t('products.new')}
      onClose={onClose}
    >
      {error && <p className="field-error">{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
        <ImageUploadField
          label={name}
          color={category?.color ?? 'var(--accent)'}
          imageUrl={imageUrl}
          onImageUrlChange={setImageUrl}
          disabled={saving}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', flex: 1 }}>
          <label htmlFor="product-name">
            {t('products.name')}
            <input
              id="product-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={saving}
            />
          </label>
          <label htmlFor="product-sku">
            {t('products.sku')}
            <input id="product-sku" value={sku} onChange={(e) => setSku(e.target.value)} required disabled={saving} />
          </label>
          {duplicateFrom && <span className="field-hint">{t('products.duplicateSkuHint')}</span>}
        </div>
      </div>

      <label htmlFor="product-category">
        {t('products.category')}
        <select
          id="product-category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          disabled={saving}
        >
          <option value="">{t('products.allCategories')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <div style={{ flex: 1 }}>
          <PriceInput
            label={optionsEnabled ? t('products.options.fromPrice') : t('products.price')}
            valueCents={optionsEnabled ? derivedPrice : priceCents}
            onChange={(cents, valid) => {
              setPriceCents(cents);
              setPriceValid(valid);
            }}
            disabled={saving || optionsEnabled}
          />
        </div>
        <div style={{ flex: 1 }}>
          <PriceInput
            label={t('products.cost')}
            valueCents={costCents ?? null}
            onChange={(cents, valid) => {
              setCostCents(cents);
              // Blank reports valid (a legal "unknown cost"); mistyped text reports invalid
              // and blocks the save instead of being sent as "unknown" (reviewed).
              setCostValid(valid);
            }}
            disabled={saving}
          />
          <span className="field-hint">{t('products.costHint')}</span>
        </div>
        <div style={{ width: 110 }}>
          <label htmlFor="product-sort">
            {t('products.sortOrder')}
            <input
              id="product-sort"
              type="number"
              className="numeric"
              style={{ textAlign: 'right', width: '100%' }}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              disabled={saving}
            />
          </label>
        </div>
      </div>

      <label htmlFor="product-kitchen" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          id="product-kitchen"
          type="checkbox"
          checked={needsKitchen}
          onChange={(e) => setNeedsKitchen(e.target.checked)}
          disabled={saving}
        />
        {t('products.needsKitchen')}
      </label>
      <span className="field-hint">{t('products.needsKitchenHint')}</span>

      <ProductOptionsEditor
        enabled={optionsEnabled}
        onEnabledChange={setOptionsEnabled}
        label={optionsLabel}
        onLabelChange={setOptionsLabel}
        max={optionsMax}
        onMaxChange={setOptionsMax}
        maxInvalid={!maxOk}
        addonsMax={addonsMax}
        onAddonsMaxChange={setAddonsMax}
        addonsMaxInvalid={!addonsMaxOk}
        mixMin={mixMin}
        onMixMinChange={setMixMin}
        mixPriceCents={mixPriceCents}
        onMixPriceChange={(cents, valid) => {
          setMixPriceCents(cents);
          setMixPriceValid(valid);
        }}
        mixInvalid={!mixOk}
        drafts={optionDrafts}
        onDraftsChange={setOptionDrafts}
        disabled={saving}
      />

      {optionsEnabled ? null : (
        <StockInput
          label={persisted ? t('products.stockAdjustLabel') : t('products.stockOpeningLabel')}
          valueQty={stockQty}
          onChange={(qty, valid) => {
            setStockQty(qty);
            setStockValid(valid);
          }}
          disabled={saving}
        />
      )}

      {persisted && (
        <label htmlFor="product-active" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input
            id="product-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            disabled={saving}
          />
          {t('common.active')}
        </label>
      )}

      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!canSave} onClick={() => void submit()}>
          {saving ? t('common.loading') : t('products.save')}
        </button>
      </div>
    </Modal>
  );
}
